import {
  parsePowerConsumption,
} from '@/data/models/BaseDeviceInfo'
import { GenericDevice, NewGenericDevice } from '@/data/models/GenericDevice'
import {
  getCategoryIdFromName,
  getDeviceAttributeType,
  getDeviceTemplateIdFromName,
  getTemplate,
} from '@/data/repository/GenericDeviceRepository'
import { DevicePowerUsageType, getDeviceAttributeTypeFromDeviceTemplate, getDevicePowerUsageNumber } from '@/data/models/DeviceAttribute'
import {CSV_SEPARATOR, formatNumber, getFileNameWithoutExtension, isNotEmpty} from '@/utils/Utils'
import { findBranchByNumber } from '@/data/repository/ClientRepository'
import { getDeviceHomeLocation } from '@/utils/VehicleUtils'
import { timestampToDate } from './DateFormatter'
import { firestore } from 'firebase'
import {
  formatDateToDayMonthYear,
  parseDateFromDayMonthYear,
} from './DateHelper'
import { DeviceSettingsJson, getCategoryIdFromSerialNumber, getTemplateIdFromSerialNumber } from '@/data/models/DeviceSettingsManager'
import { parseNumber } from './NumberParser'

export const DUPLICATE_STATUS_KEY_ID = "0\t";

export enum ImportType {
  UPDATE_EXISTING_IRES_DEVICE,
  UPDATE_EXISTING_GENERIC_DEVICE,
  CREATE_GENERIC_DEVICE
}

export const CSV_HEADERS = [
  "KATEGORIE",
  "TYP",
  "INVENTARNUMMER",
  "SERIENNUMMER",
  "HERSTELLER",
  "CODE",
  "LEISTUNG",
  "ZÄHLER",
  "STROMZÄHLERHERSTELLER",
  "STROMZÄHLERNUMMER",
  "ANSCHAFFUNGSDATUM",
  "VERFÜGBARKEIT",
  "STANDORT",
  "KOMMENTAR",
  "ZÄHLERSTAND",
  "STANDORTNAME",
];

/** i18n keys for CSV headers, ordered to match CSV_HEADERS / ValueType indices */
const CSV_HEADER_I18N_KEYS = [
  "settings.hyDryDevice.csvHeaders.category",
  "settings.hyDryDevice.csvHeaders.type",
  "settings.hyDryDevice.csvHeaders.inventoryNumber",
  "settings.hyDryDevice.csvHeaders.serialNumber",
  "settings.hyDryDevice.csvHeaders.manufacturer",
  "settings.hyDryDevice.csvHeaders.code",
  "settings.hyDryDevice.csvHeaders.power",
  "settings.hyDryDevice.csvHeaders.meter",
  "settings.hyDryDevice.csvHeaders.electricityMeterManufacturer",
  "settings.hyDryDevice.csvHeaders.electricityMeterNumber",
  "settings.hyDryDevice.csvHeaders.acquisitionDate",
  "settings.hyDryDevice.csvHeaders.availability",
  "settings.hyDryDevice.csvHeaders.location",
  "settings.hyDryDevice.csvHeaders.comment",
  "settings.hyDryDevice.csvHeaders.meterReading",
  "settings.hyDryDevice.csvHeaders.locationName",
];

/**
 * Returns localized CSV column headers using vue-i18n.
 * Falls back to German CSV_HEADERS if translation is not available.
 */
export function getLocalizedCSVHeaders(translate: (key: string) => string): string[] {
  return CSV_HEADER_I18N_KEYS.map((key, index) => {
    const translated = translate(key);
    return translated !== key ? translated : CSV_HEADERS[index];
  });
}

export const CSV_REPORT_HEADERS: string[] = [...CSV_HEADERS, "STATUS"];
/**
 * Contains the header values of the CSV file in the same order
 */
export enum ValueType {
  CATEGORY,
  TEMPLATE,

  INVENTORY_NUMBER,
  SERIAL_NUMBER,
  MANUFACTURER,
  QR_CODE,

  POWER_CONSUMPTION,
  METER,
  ELECTRICITY_METER_MANUFACTURER,
  ELECTRICITY_METER_NUMBER,

  ACQUISITION_DATE,
  AVAILABILITY,
  BRANCH,
  NOTE,

  LATEST_METER_READING,
  /**
   * Name of the branch named by BRANCH. Written on export so the file is readable
   * without the branch list at hand; ignored on import, where BRANCH is authoritative.
   */
  LOCATION_NAME,
}

export const MAX_EXPECTED_COLUMNS = Object.values(ValueType).length / 2;
export const MIN_EXPECTED_COLUMNS = 4;

/**
 * Generates a row for the CSV, parsing the device data and ordering it as per ValueType ordering.
 * @param categoryName
 * @param templateName
 * @param deviceInfo
 * @param latestMeterReading
 */
export function createLine(
  deviceSettingsJson: DeviceSettingsJson,
  categoryName: string,
  templateName: string,
  deviceInfo: NewGenericDevice | GenericDevice,
  latestMeterReading?: number | null,
  isIresDevice?: boolean,
): string[] {
  const line: string[] = Array(ValueType.LOCATION_NAME + 1);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const add = (type: ValueType, value: any) => {
    line[type] = value !== undefined ? value?.toString().trim() : "";
  };

  const template = getTemplate(
    deviceSettingsJson,
    isIresDevice && deviceInfo.serialNumber ? getCategoryIdFromSerialNumber(deviceInfo.serialNumber) : deviceInfo.categoryId,
    isIresDevice && deviceInfo.serialNumber ? getTemplateIdFromSerialNumber(deviceInfo.serialNumber) : deviceInfo.templateId
  )
  const selectedPowerConsumption = template
    ? getDevicePowerUsageNumber(
        getDeviceAttributeTypeFromDeviceTemplate(
          template
        )
      )
    : 0;

  const powerConsumptionPerHour = deviceInfo.powerConsumptionPerHour;

  add(ValueType.CATEGORY, categoryName);
  add(ValueType.TEMPLATE, templateName);

  add(ValueType.INVENTORY_NUMBER, deviceInfo.inventoryNumber);
  add(ValueType.SERIAL_NUMBER, deviceInfo.serialNumber);
  add(ValueType.MANUFACTURER, deviceInfo.manufacturer);
  add(ValueType.QR_CODE, deviceInfo.qrCode);

  add(ValueType.POWER_CONSUMPTION, powerConsumptionPerHour ? formatNumber(powerConsumptionPerHour, false) : powerConsumptionPerHour);
  add(ValueType.METER, selectedPowerConsumption);
  add(
    ValueType.ELECTRICITY_METER_MANUFACTURER,
    deviceInfo.electricityMeterManufacturer
  );
  add(
    ValueType.ELECTRICITY_METER_NUMBER,
    deviceInfo.electricityMeterNumber
  );

  add(
    ValueType.ACQUISITION_DATE,
    deviceInfo.acquisitionDate
      ? formatDateToDayMonthYear(
          timestampToDate(deviceInfo.acquisitionDate)
        )
      : null
  );
  add(ValueType.AVAILABILITY, deviceInfo.availability);

  // The STANDORT column carries the branch the device belongs to, not the one it
  // happens to stand in, so an inventory file survives a device being loaded onto a
  // vehicle or unloaded at another warehouse.
  const homeLocation = getDeviceHomeLocation(deviceInfo);
  add(ValueType.BRANCH, homeLocation);
  add(ValueType.NOTE, deviceInfo.note);

  add(ValueType.LATEST_METER_READING, latestMeterReading ? formatNumber(latestMeterReading, false) : latestMeterReading);
  add(ValueType.LOCATION_NAME, findBranchByNumber(homeLocation)?.name);

  return line;
}

/**
 * Reads CSV file and parses data into a NewGenericDevice
 * The purpose of this function is to only parse a line into an object, no consistent checks are performed
 * Data read according to order of ValueType ENUM, and it is assumed that the row data follows the same order
 * @param deviceSettingsJson
 * @param row
 */
export function parseLine(
  deviceSettingsJson: DeviceSettingsJson, 
  row: string | string[],
  noOfColumnsToBeConsidered: number,
): NewGenericDevice {
  let inventoryNumber = ''
  let serialNumber: string | undefined
  let comment: string | undefined
  let devicePowerUsageType: DevicePowerUsageType = DevicePowerUsageType.NONE
  let powerConsumptionPerHour: number | undefined
  let meterReading: number | undefined
  let electricityMeterManufacturer: string | undefined
  let electricityMeterNumber: string | undefined
  let manufacturer: string | undefined
  let acquisitionDate: firestore.Timestamp | undefined
  let availability: number | undefined
  let homeLocation: string | undefined
  let qrCode: string | undefined
  let templateName = ''
  let categoryName = ''
  
  let index = 1;
  for (const value in ValueType) {
    if (index > noOfColumnsToBeConsidered) {
      break;
    }

    if (!isNaN(Number(value))) {
      const key: ValueType = Number(value);
      
      /** Trim string CSV values to remove leading/trailing whitespace */
      const rawValue = typeof row[Number(key)] === 'string' ? row[Number(key)].trim() : row[Number(key)];

      switch (key) {
        case ValueType.CATEGORY:
          {
            categoryName = rawValue;
          }
          break;
        case ValueType.TEMPLATE:
          {
            templateName = rawValue;
          }
          break;
        case ValueType.INVENTORY_NUMBER:
          {
            inventoryNumber = rawValue;
          }
          break;
        case ValueType.SERIAL_NUMBER:
          {
            serialNumber = rawValue;
          }
          break;
        case ValueType.MANUFACTURER:
          {
            manufacturer = rawValue;
          }
          break;
        case ValueType.QR_CODE:
          {
            qrCode = rawValue;
          }
          break;
        case ValueType.POWER_CONSUMPTION:
          {
            powerConsumptionPerHour =
              parsePowerConsumption(rawValue) ?? undefined;
          }
          break;
        case ValueType.METER:
          {
            const powerConsumption = rawValue;
            switch (powerConsumption) {
              case "1":
                devicePowerUsageType = DevicePowerUsageType.OPERATING_HOURS;
                break;
              case "2":
                devicePowerUsageType = DevicePowerUsageType.ELECTRICITY_METER;
                break;
              case "3":
                devicePowerUsageType =
                  DevicePowerUsageType.ELECTRICITY_METER_FOR_OTHER_DEVICES;
                break;
              case "4":
                devicePowerUsageType =
                  DevicePowerUsageType.OPERATING_HOURS_FROM_ASSIGNMENT;
                break;
              default:
                devicePowerUsageType = DevicePowerUsageType.NONE;
            }
          }
          break;
        case ValueType.ELECTRICITY_METER_MANUFACTURER:
          {
            electricityMeterManufacturer =
              rawValue;
          }
          break;
        case ValueType.ELECTRICITY_METER_NUMBER:
          {
            electricityMeterNumber =
              rawValue;
          }
          break;
        case ValueType.ACQUISITION_DATE:
          {
            acquisitionDate = parseDateFromDayMonthYear(
              rawValue
            ) ?? undefined;
          }
          break;
        case ValueType.AVAILABILITY:
          {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            let value: any = Number(rawValue);
            if (isNaN(value)) {
              value = undefined;
            }
            availability = value;
          }
          break;
        case ValueType.BRANCH:
          {
            homeLocation = rawValue;
          }
          break;
        case ValueType.NOTE:
          {
            comment = rawValue;
          }
          break;
        case ValueType.LATEST_METER_READING:
          {
            meterReading = parseNumber(rawValue) ?? undefined;
          }
          break;
      }

      index++;
    }
  }

  const categoryId = getCategoryIdFromName(deviceSettingsJson, categoryName) ?? "";
  const templateId = getDeviceTemplateIdFromName(deviceSettingsJson, categoryId, templateName) ?? "";
  const selectedPowerConsumption = getDeviceAttributeType(devicePowerUsageType) ?? undefined;

  return {
    categoryName: categoryName,
    internalNumber: 0,
    templateId: templateId,
    categoryId: categoryId,
    deviceName: templateName,
    inventoryNumber: inventoryNumber,
    serialNumber: isNotEmpty(serialNumber) ? serialNumber : undefined,
    manufacturer: isNotEmpty(manufacturer) ? manufacturer : undefined,
    powerConsumptionPerHour: powerConsumptionPerHour,
    electricityMeterManufacturer: isNotEmpty(electricityMeterManufacturer) ? electricityMeterManufacturer : undefined,
    selectedPowerConsumption: selectedPowerConsumption,
    electricityMeterNumber: isNotEmpty(electricityMeterNumber) ? electricityMeterNumber : undefined,
    acquisitionDate: acquisitionDate,
    availability: availability,
    lastMeterReading: meterReading,
    homeLocation: isNotEmpty(homeLocation) ? homeLocation : undefined,
    note: isNotEmpty(comment) ? comment : undefined ,
    qrCode: isNotEmpty(qrCode) ? qrCode : undefined,
  };
}

export function isCSVFile(file: File): boolean {
  const fileName = file.name.toLowerCase();
  return fileName.endsWith(".csv");
}

/**
 * Detects the encoding of a CSV file buffer by checking for BOM markers,
 * then decodes the content. Falls back to windows-1252 if UTF-8 decoding fails.
 * This handles CSV files exported from Excel which often use windows-1252 encoding
 * with German special characters (ä, ö, ü, ß).
 */
export function decodeCSVBuffer(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);

  // UTF-8 BOM (EF BB BF)
  if (bytes.length >= 3 && bytes[0] === 0xEF && bytes[1] === 0xBB && bytes[2] === 0xBF) {
    return new TextDecoder("utf-8").decode(bytes.subarray(3));
  }

  // UTF-16 LE BOM (FF FE)
  if (bytes.length >= 2 && bytes[0] === 0xFF && bytes[1] === 0xFE) {
    return new TextDecoder("utf-16le").decode(bytes.subarray(2));
  }

  // UTF-16 BE BOM (FE FF)
  if (bytes.length >= 2 && bytes[0] === 0xFE && bytes[1] === 0xFF) {
    return new TextDecoder("utf-16be").decode(bytes.subarray(2));
  }

  // No BOM — try UTF-8 with fatal flag, fall back to windows-1252
  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch {
    return new TextDecoder("windows-1252").decode(bytes);
  }
}

export function downloadReport(
  document: Document,
  fileName: string,
  report: string[]
) {
  const csvContent = [CSV_REPORT_HEADERS.join(CSV_SEPARATOR), ...report].join(
    "\n"
  );
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const objectUrl = URL.createObjectURL(blob);
  link.href = objectUrl;
  // eslint-disable-next-line
  link.download = `${getFileNameWithoutExtension(fileName!)}-P`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(objectUrl);
}
